"use client";

import { Check, Circle, CircleNotch, GitCommit, GitFork } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "motion/react";

import { RadioGroup, RadioGroupItem } from "@shared/ui/radio-group";
import { cn } from "@shared/ui/utils";

import { CustomCard } from "~/components/custom-card";
import { Section } from "~/components/section";

const options = [
  {
    size: "Small",
    label: "1 CPU · 2 GB RAM",
    description: "For lightweight jobs, unit tests",
  },
  {
    size: "Medium",
    label: "2 CPU · 4 GB RAM",
    description: "For integration tests, small builds",
  },
  {
    size: "Large",
    label: "4 CPU · 8 GB RAM",
    description: "For larger builds, resource-heavy workflows",
  },
];

const jobs = [
  {
    title: "Testing",
    time: "23s",
    delay: 0,
    times: [0, 0.02, 0.18, 0.2, 1, 1],
    duration: 10,
  },
  {
    title: "Linting",
    time: "12s",
    delay: 2,
    times: [0, 0.02, 0.225, 0.25, 1, 1],
    duration: 8,
  },
  {
    title: "Format Check",
    time: "15s",
    delay: 4,
    times: [0, 0.02, 0.3, 0.3333, 1, 1],
    duration: 6,
  },
  {
    title: "ChangeSet",
    time: "9s",
    delay: 6,
    times: [0, 0.02, 0.45, 0.5, 1, 1],
    duration: 4,
  },
  {
    title: "ChangeSet2",
    time: "11s",
    delay: 8,
    times: [0, 0.02, 0.45, 0.6, 1, 1],
    duration: 2,
  },
];

const RunnerOption = ({ option }: { option: (typeof options)[number] }) => {
  return (
    <div className="rounded-xl outline outline-offset-8 outline-white/20">
      <label
        className="bg-layer-1 has-[button[data-state=checked]]:bg-basic-card-hovered hover:bg-basic-card-hovered has-[button[data-state=checked]]:border-sticker-active flex gap-3 rounded-xl border border-transparent p-4 outline-8 outline-white/[8%] transition-all duration-300 hover:cursor-pointer hover:border"
        htmlFor={option.size}
      >
        <RadioGroupItem
          value={option.size}
          id={option.size}
          className="border-sticker-inactive-border data-[state=checked]:border-sticker-active data-[state=checked]:text-sticker-active cursor-pointer"
        />
        <div className="text-basic-card-primary-foreground flex flex-col gap-2 text-xs">
          <span>{`${option.size} ${option.label}`}</span>
          <p className="text-static-secondary">{option.description}</p>
        </div>
      </label>
    </div>
  );
};

export const RunnersInfo = () => {
  return (
    <Section title="CI/CD Runners engineered for DevOps success">
      <CustomCard
        Icon={<GitCommit weight="fill" size={24} />}
        title="Choose the perfect fit for every workload"
        description="Teams have different needs. Choose optimized presets to get the most out of your cloud budget."
        contentClass="pt-8"
        cardClass="h-[666px]"
      >
        <RadioGroup className="gap-11" defaultValue="Medium">
          {options.map((option) => {
            return <RunnerOption key={option.size} option={option} />;
          })}
        </RadioGroup>
      </CustomCard>
      <CustomCard
        Icon={<GitFork weight="fill" size={24} className="-rotate-90" />}
        title="Accelerate with modern, high-performance infrastructure"
        description="Speed up your deployments with Tenki’s reliable cloud infrastructure, designed to streamline your workflows and ensure your DevOps budget stays on track without unexpected costs."
        contentClass="pr-0"
        cardClass="h-[666px]"
      >
        <div className="rounded-tl-2xl border-t border-l border-white/20">
          <div className="rounded-tl-2xl border-t-8 border-l-8 border-white/[8%] px-10 pt-10 lg:px-[72px]">
            <div className="flex">
              <div className="text-md text-static-secondary border-neutral-900-border rounded-t-lg border-t border-l px-7 py-3.5 font-medium">
                Jobs
              </div>
              <div className="border-neutral-900-border h-[49px] w-[90%] -translate-x-[1.5px] translate-y-[5.5px] rounded-bl-lg border-b border-l" />
            </div>
            <ul className="border-neutral-900-border flex flex-col items-center justify-center gap-2 rounded-tr-lg border-x px-3 pt-3">
              <AnimatePresence>
                {jobs.map((job, index) => {
                  const { duration, delay, times } = job;
                  const transition = {
                    duration,
                    repeat: Infinity,
                    times,
                    delay,
                    repeatDelay: delay,
                  };
                  return (
                    <motion.li
                      key={index}
                      initial={{ borderColor: "#0b1118", background: "#121A23", opacity: 0.4 }}
                      animate={{
                        borderColor: ["#0b1118", "#5184dc", "#5184dc", "#0b1118", "#0b1118", "#121a23"],
                        background: ["#121a23", "#1e2a39", "#1e2a39", "#121a23", "#121a23", "#121a23"],
                        opacity: [0.4, 1, 1, 1, 1, 0.4],
                      }}
                      transition={transition}
                      className={cn(
                        "bg-layer-2 flex h-[52px] w-full items-center justify-between rounded-t-md border px-4",
                        index !== jobs.length - 1 && "rounded-b-md",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <div>
                          <motion.div
                            initial={{ opacity: 1 }}
                            animate={{
                              opacity: [1, 0, 0, 0, 0, 1],
                              y: [20, 20, 20, 20, 20, 20],
                            }}
                            transition={transition}
                            custom={job.delay}
                          >
                            <Circle size={20} weight="bold" className="text-sticker-inactive-border" />
                          </motion.div>

                          <motion.div
                            initial={{ opacity: 1 }}
                            animate={{
                              opacity: [0, 1, 1, 0, 0, 0],
                            }}
                            transition={transition}
                          >
                            <CircleNotch size={20} weight="bold" className="animate-spin" />
                          </motion.div>
                          <motion.div
                            initial={{ opacity: 1 }}
                            animate={{
                              opacity: [0, 0, 0, 1, 1, 0],
                              y: [-20, -20, -20, -20, -20, -20],
                            }}
                            transition={transition}
                            className="bg-sticker-success size-5 items-center justify-center rounded-full border"
                          >
                            <Check size={16} weight="bold" className="p-px pt-[2px] pl-[3px] text-white" />
                          </motion.div>
                        </div>
                        <motion.div className={cn("flex-1 text-sm")}>{job.title}</motion.div>
                      </div>

                      <div className="flex justify-end">
                        <motion.div
                          initial={{
                            opacity: 0,
                          }}
                          animate={{
                            opacity: [0, 1, 1, 0, 0, 0],
                            x: [20, 20, 20, 20, 20, 20],
                          }}
                          transition={transition}
                          className="text-static-secondary text-sm"
                        >
                          Running...
                        </motion.div>
                        <motion.div
                          initial={{
                            opacity: 0,
                          }}
                          animate={{
                            opacity: [0, 0, 0, 1, 1, 0],
                          }}
                          transition={transition}
                          className="text-static-secondary text-sm"
                        >
                          {job.time}
                        </motion.div>
                      </div>
                    </motion.li>
                  );
                })}
              </AnimatePresence>
            </ul>
          </div>
        </div>
      </CustomCard>
    </Section>
  );
};
